<template>
  <div class="bg-cover bg-center bg-no-repeat bg-custom-login-image">
    <div class="flex items-center justify-center h-screen px-6 bg-gray-900/80">
      <div class="max-w-xl p-6 bg-blue-900 shadow-md w-[400px] rounded-[16px]">
        <div class="flex items-center justify-center mb-4">
          <img
            class="object-cover w-[100px] h-[60px]"
            src="@/assets/images/Jiji-Health-White-Logo.png"
            alt="Jiji Health Logo"
          />
        </div>
        <div class="flex justify-center font-thin text-white text-xs mb-2">
          <div>Welcome to Jiji Health,</div>
        </div>
        <div class="flex justify-center font-thin text-white text-xs mb-6">
          <div>
            Advancing <span class="text-orange-400">Healthcare</span> Tech!
          </div>
        </div>

        <!-- Stepper -->
        <div class="mb-6">
          <div class="flex justify-between mb-2">
            <div
              :class="[
                'w-1/3 text-center cursor-pointer transition-all duration-200',
                { 'text-orange-300': currentStep >= 1 },
                { 'hover:text-orange-200': currentStep > 1 }
              ]"
              @click="currentStep > 1 ? goBackToSystemSelection() : null"
              :title="currentStep > 1 ? 'Click to change system' : ''"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-full text-white flex items-center justify-center mx-auto mb-1 transition-all duration-200',
                  currentStep >= 1 ? 'bg-orange-300' : 'bg-gray-400',
                  currentStep > 1 ? 'hover:bg-orange-200 cursor-pointer' : ''
                ]"
              >
                1
              </div>
              <div class="relative">
                Select System
                <span v-if="currentStep > 1" class="text-xs text-orange-200 ml-1">(clickable)</span>
              </div>
            </div>
            <div
              :class="[
                'w-1/3 text-center',
                { 'text-orange-300': currentStep >= 2 },
              ]"
            >
              <div
                :class="[
                  'w-8 h-8 rounded-full text-white flex items-center justify-center mx-auto mb-1',
                  currentStep >= 2 ? 'bg-orange-300' : 'bg-gray-400'
                ]"
              >
                2
              </div>
              <div>Login</div>
            </div>
          </div>
          <div class="w-full bg-gray-300 h-1">
            <div
              :style="{ width: `${(currentStep / 2) * 100}%` }"
              class="h-1 bg-orange-300 transition-all duration-300"
            ></div>
          </div>
        </div>

        <!-- Step 1: System Selection -->
        <div v-if="currentStep === 1" class="text-center">
          <h3 class="text-white text-lg mb-4">Select System</h3>
          <button
            @click="selectSystem('eLearning')"
            class="w-full px-4 py-2 mb-2 bg-orange-300 text-gray-800 rounded hover:bg-orange-400"
          >
            E-Learning
          </button>
          <button
            @click="selectSystem('hmis')"
            class="w-full px-4 py-2 mb-2 bg-orange-300 text-gray-800 rounded hover:bg-orange-400"
          >
            Doctor Bookings
          </button>
          <button
            @click="selectSystem('rafiki360')"
            class="w-full px-4 py-2 mb-2 bg-orange-300 text-gray-800 rounded hover:bg-orange-400"
          >
            Rafiki360
          </button>
          <button
            @click="selectSystem('appeals')"
            class="w-full px-4 py-2 bg-orange-300 text-gray-800 rounded hover:bg-orange-400"
          >
            Appeals $ Donations
          </button>
        </div>

        <!-- Step 2: Login Form -->
        <div v-if="currentStep === 2">
          <!-- Back to System Selection Button -->
          <div class="mb-4 text-center">
            <button
              @click="goBackToSystemSelection"
              class="inline-flex items-center px-3 py-2 text-sm text-orange-300 hover:text-orange-200 transition-colors duration-200"
            >
              <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
              </svg>
              ← Back to System Selection
            </button>
          </div>

          <!-- Error Alert -->
          <transition name="fade">
            <div
              v-if="errorMessage"
              class="p-4 mb-4 text-sm text-red-800 rounded-md bg-orange-50 dark:text-red-400"
              role="alert"
            >
              <span class="font-medium">{{ errorMessage }}</span>
            </div>
          </transition>

          <form @submit.prevent="login" class="px-3">
            <label class="block">
              <span class="text-sm text-white">Email or Phone</span>
              <input
                type="text"
                class="block w-full mt-1 px-3 py-2 dark:placeholder-gray-400 rounded-md"
                v-model="email"
                placeholder="johndoe@mail.com or 0712345678"
              />
            </label>

            <label class="block mt-4 relative">
              <span class="text-sm text-white">Password</span>
              <input
                ref="passwordField"
                :type="passwordVisible ? 'text' : 'password'"
                class="block w-full mt-1 px-3 py-2 dark:placeholder-gray-400 rounded-md"
                v-model="password"
                placeholder="your password"
              />
              <div
                class="absolute inset-y-4 py-8 right-0 pr-3 flex items-center text-sm leading-5"
              >
                <button
                  type="button"
                  id="togglePassword"
                  class="text-gray-500 focus:outline-none focus:text-gray-600 hover:text-gray-600"
                  @click="togglePasswordVisibility"
                >
                  <svg
                    v-if="passwordVisible"
                    class="w-6 h-6 text-gray-800 dark:text-gray-500"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      d="m4 15.6 3.055-3.056A4.913 4.913 0 0 1 7 12.012a5.006 5.006 0 0 1 5-5c.178.009.356.027.532.054l1.744-1.744A8.973 8.973 0 0 0 12 5.012c-5.388 0-10 5.336-10 7A6.49 6.49 0 0 0 4 15.6Z"
                    />
                    <path
                      d="m14.7 10.726 4.995-5.007A.998.998 0 0 0 18.99 4a1 1 0 0 0-.71.305l-4.995 5.007a2.98 2.98 0 0 0-.588-.21l-.035-.01a2.981 2.981 0 0 0-3.584 3.583c0 .012.008.022.01.033.05.204.12.402.211.59l-4.995 4.983a1 1 0 1 0 1.414 1.414l4.995-4.983c.189.091.386.162.59.211.011 0 .021.007.033.01a2.982 2.982 0 0 0 3.584-3.584c0-.012-.008-.023-.011-.035a3.05 3.05 0 0 0-.21-.588Z"
                    />
                    <path
                      d="m19.821 8.605-2.857 2.857a4.952 4.952 0 0 1-5.514 5.514l-1.785 1.785c.767.166 1.55.25 2.335.251 6.453 0 10-5.258 10-7 0-1.166-1.637-2.874-2.179-3.407Z"
                    />
                  </svg>
                  <svg
                    v-else
                    class="w-6 h-6 text-gray-500"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke="currentColor"
                      stroke-width="2"
                      d="M21 12c0 1.2-4.03 6-9 6s-9-4.8-9-6c0-1.2 4.03-6 9-6s9 4.8 9 6Z"
                    />
                    <path
                      stroke="currentColor"
                      stroke-width="2"
                      d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
                    />
                  </svg>
                </button>
              </div>
            </label>

            <div class="mt-6">
              <button
                type="submit"
                class="w-full font-semibold leading-relaxed px-4 py-2 text-md text-center text-white bg-orange-300 rounded-md focus:outline-none hover:text-blue-900"
              >
                Log in
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { AuthAPI } from "@/services/auth";
import { setApiSystem } from "@/services/api";

export default {
  name: "LoginPage",
  data() {
    return {
      email: "",
      password: "",
      passwordVisible: false,
      errorMessage: "",
      currentStep: 1,
      system: null,
    };
  },
  setup() {
    const store = useStore();
    const router = useRouter();
    const toast = useToast();

    return { store, router, toast };
  },
  created() {
    const urlParams = new URLSearchParams(window.location.search);
    const systemParam = urlParams.get("system");
    if (systemParam && ["eLearning", "hmis", "appeals"].includes(systemParam)) {
      this.system = systemParam;
      this.currentStep = 2;
      setApiSystem(this.system);
    } else {
      this.system = "eLearning"; // Default to eLearning
    }
  },
  methods: {
    selectSystem(selectedSystem) {
      if (!["eLearning", "hmis", "rafiki360", "appeals"].includes(selectedSystem)) {
        this.errorMessage = "Invalid system selected.";
        this.hideErrorMessage();
        return;
      }
      this.system = selectedSystem;
      this.currentStep = 2;
      setApiSystem(this.system);
    },
    goBackToSystemSelection() {
      // Clear form data and error message
      this.email = "";
      this.password = "";
      this.passwordVisible = false;
      this.errorMessage = "";
      this.system = null;
      
      // Go back to step 1
      this.currentStep = 1;
    },
    validateFields() {
      const emptyFields = [];
      if (!this.email) emptyFields.push("Email/Phone Number");
      if (!this.password) emptyFields.push("Password");
      return emptyFields;
    },
    async login() {
      this.errorMessage = "";

      // Validate fields
      const emptyFields = this.validateFields();
      if (emptyFields.length > 0) {
        this.errorMessage = `Please fill the following field(s): ${emptyFields.join(
          ", "
        )}`;
        this.hideErrorMessage();
        return;
      }

      // Validate system
      if (!["eLearning", "hmis", "rafiki360", "appeals"].includes(this.system)) {
        this.errorMessage = "Please select a system before logging in.";
        this.currentStep = 1;
        this.hideErrorMessage();
        return;
      }

      try {
        console.log(
          "Attempting login with system:",
          this.system,
          "email:",
          this.email
        );
        setApiSystem(this.system); // Ensure API system is set
        const response = await AuthAPI.login(
          { email: this.email, password: this.password },
          this.system // Pass system parameter
        );

        console.log("Login response:", response);

        const { user, token } = response; // Use normalized response
        if (!user || !token) {
          throw new Error("Invalid response: Missing user or token");
        }

        console.log(
          "Dispatching login with system:",
          this.system,
          "user:",
          user,
          "token:",
          token
        );
        await this.store.dispatch("login", {
          system: this.system,
          data: user,
          token,
        });

        console.log("Store state after login:", this.store.state);

        // Show success toast message
        const systemDisplayNames = {
          eLearning: 'E-Learning',
          hmis: 'Doctor Bookings',
          rafiki360: 'Rafiki360',
          appeals: 'Appeals $ Donations'
        };
        const displayName = systemDisplayNames[this.system] || this.system;
        console.log(`Successfully logged into ${displayName}! Welcome back, ${user.first_name || user.email || 'User'}!`);

        // Clear intended route if it exists
        await this.store.commit("clearIntendedRoute");

        let redirectPath = this.system === "hmis"
          ? "/doctor-bookings"
          : this.system === "rafiki360"
          ? "/rafiki360"
          : this.system === "appeals"
          ? "/appeals-donations"
          : "/e-learning";
        const intended = this.store.getters.intendedRoute;
        if (intended && intended.startsWith(redirectPath)) {
          redirectPath = intended;
        }
        console.log("Redirecting to:", redirectPath);
        await this.router.push(redirectPath).catch((err) => {
          console.error("Navigation failed:", err);
          window.location.href = redirectPath; // Fallback navigation
        });
      } catch (error) {
        console.error("Login error:", error.response?.data || error.message);
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Wrong email or password";
        this.hideErrorMessage();
      }
    },
    hideErrorMessage() {
      setTimeout(() => {
        this.errorMessage = "";
      }, 3500);
    },
    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
      const passwordField = this.$refs.passwordField;
      if (passwordField) {
        passwordField.type = this.passwordVisible ? "text" : "password";
      }
    },
  },
};
</script>

<style scoped></style>
